import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { UserRole } from '@alpha-edge/shared'
import { api, setAuthToken, ApiError } from '../services/api'
import { invalidateOnboardingStatus } from './use-onboarding-status'
import { invalidateFeatures } from './use-features.js'

interface AuthUser {
  readonly id: string
  readonly email: string
  readonly firstName: string
  readonly lastName: string
  readonly role: UserRole
  readonly isDefault?: boolean
  readonly organizationId?: string
}

interface AuthState {
  readonly user: AuthUser | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly needsOnboarding: boolean
  readonly isImpersonating: boolean
  readonly impersonatorEmail?: string
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  startImpersonation: (userId: string) => Promise<void>
  stopImpersonation: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_KEY = 'alpha_edge_token'
const ADMIN_TOKEN_KEY = 'alpha_edge_admin_token'

export function AuthProvider({ children }: { children: ReactNode }) {
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem(TOKEN_KEY)

  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: hasToken,
    error: null,
    needsOnboarding: false,
    isImpersonating: false,
  })

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return

    setAuthToken(token)
    api
      .get<{
        id?: string
        sub?: string
        email: string
        role: UserRole
        isDefault?: boolean
        organizationId?: string
        needsOnboarding?: boolean
        impersonatorId?: string
        impersonatorEmail?: string
      }>('/auth/me')
      .then((res) => {
        if (res.data) {
          const isImpersonating = Boolean(res.data.impersonatorId)
          setState({
            user: {
              id: res.data.id ?? res.data.sub ?? '',
              email: res.data.email,
              firstName: '',
              lastName: '',
              role: res.data.role,
              isDefault: res.data.isDefault,
              organizationId: res.data.organizationId,
            },
            isLoading: false,
            error: null,
            needsOnboarding: Boolean(res.data.needsOnboarding),
            isImpersonating,
            impersonatorEmail: isImpersonating ? res.data.impersonatorEmail : undefined,
          })
        }
      })
      .catch((err: unknown) => {
        // Only a genuine auth failure (401/403) means the token is invalid and
        // should be discarded. A transient failure -- 429 during a rate-limit
        // storm, a 5xx, or a network blip -- must NOT delete a still-valid
        // token, or a page refresh would force an unnecessary re-login.
        const status = err instanceof ApiError ? err.status : undefined
        const isAuthFailure = status === 401 || status === 403
        if (isAuthFailure) {
          localStorage.removeItem(TOKEN_KEY)
          setAuthToken(null)
        }
        setState({
          user: null,
          isLoading: false,
          error: isAuthFailure ? null : 'Could not verify your session. Please try again.',
          needsOnboarding: false,
          isImpersonating: false,
        })
      })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const res = await api.post<{ token: string; user: AuthUser; needsOnboarding?: boolean }>('/auth/login', {
        email,
        password,
      })
      if (res.data) {
        localStorage.setItem(TOKEN_KEY, res.data.token)
        setAuthToken(res.data.token)
        setState({
          user: res.data.user,
          isLoading: false,
          error: null,
          needsOnboarding: Boolean(res.data.needsOnboarding),
          isImpersonating: false,
        })
        invalidateFeatures()
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed'
      setState((prev) => ({ ...prev, isLoading: false, error: message }))
      throw err
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    setAuthToken(null)
    invalidateOnboardingStatus()
    invalidateFeatures()
    setState({
      user: null,
      isLoading: false,
      error: null,
      needsOnboarding: false,
      isImpersonating: false,
    })
  }, [])

  const startImpersonation = useCallback(async (userId: string) => {
    const currentToken = localStorage.getItem(TOKEN_KEY)
    if (currentToken) {
      localStorage.setItem(ADMIN_TOKEN_KEY, currentToken)
    }

    try {
      const res = await api.post<{ token: string; user: AuthUser }>('/auth/impersonate', { userId })
      if (res.data) {
        localStorage.setItem(TOKEN_KEY, res.data.token)
        setAuthToken(res.data.token)

        const meRes = await api.get<{
          id?: string
          sub?: string
          email: string
          role: UserRole
          impersonatorEmail?: string
        }>('/auth/me')
        if (meRes.data) {
          setState({
            user: {
              id: meRes.data.id ?? meRes.data.sub ?? '',
              email: meRes.data.email,
              firstName: res.data.user.firstName,
              lastName: res.data.user.lastName,
              role: meRes.data.role,
            },
            isLoading: false,
            error: null,
            needsOnboarding: false,
            isImpersonating: true,
            impersonatorEmail: meRes.data.impersonatorEmail,
          })
        }
        invalidateFeatures()
      }
    } catch (err) {
      localStorage.removeItem(ADMIN_TOKEN_KEY)
      const message = err instanceof ApiError ? err.message : 'Failed to start impersonation'
      setState((prev) => ({ ...prev, error: message }))
      throw err
    }
  }, [])

  const stopImpersonation = useCallback(() => {
    api.post('/auth/stop-impersonation', {}).catch(() => {})

    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY)
    if (adminToken) {
      localStorage.setItem(TOKEN_KEY, adminToken)
      localStorage.removeItem(ADMIN_TOKEN_KEY)
      setAuthToken(adminToken)

      api
        .get<{
          id?: string
          sub?: string
          email: string
          role: UserRole
          isDefault?: boolean
          needsOnboarding?: boolean
        }>('/auth/me')
        .then((res) => {
          if (res.data) {
            setState({
              user: {
                id: res.data.id ?? res.data.sub ?? '',
                email: res.data.email,
                firstName: '',
                lastName: '',
                role: res.data.role,
                isDefault: res.data.isDefault,
              },
              isLoading: false,
              error: null,
              needsOnboarding: Boolean(res.data.needsOnboarding),
              isImpersonating: false,
            })
          }
        })
        .catch(() => {
          logout()
        })
      invalidateFeatures()
    } else {
      logout()
    }
  }, [logout])

  return (
    <AuthContext.Provider value={{ ...state, login, logout, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
